/**
 * usePallasThreads — Persistent multi-turn conversation store.
 *
 * v2.5: backend-backed thread list. The sidebar's thread metadata (id, title,
 * timestamps, turn count) comes from GET /api/threads, scoped by the verified
 * Keycloak subject, so the same list appears on every browser the analyst
 * signs into. The per-turn response payload (the formatted blocks the
 * ResponsePanel renders) is NOT stored server-side — we cache it in
 * localStorage so the analyst's OWN browser keeps full visual fidelity.
 *
 * Cross-device behavior:
 *   - Thread appears in sidebar ✓   (backend list)
 *   - Click an own-device thread → full transcript restores from cache ✓
 *   - Click a thread first started on another device → empty transcript,
 *     but the backend still has its memory so the next query continues it.
 *
 * Falls back to localStorage-only if the backend is unreachable (offline
 * dev, indexer down) so the chat still works.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchThreads, fetchThreadTurns, deleteThreadOnServer, renameThreadOnServer,
} from '../services/pallasService';

const STORAGE_KEY = 'pallas-threads-v2';
// Single source of truth for the cap. Sidebar display, backend fetch, and
// localStorage cache all use the same number — change here, change everywhere.
const MAX_THREADS = 15;
const MAX_TURNS = 25;        // per thread, applied to the local turn cache
const TITLE_MAX = 48;

function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `t_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

function truncate(s) {
  const t = String(s || '').trim().replace(/\s+/g, ' ');
  if (!t) return 'New chat';
  if (t.length <= TITLE_MAX) return t;
  return t.slice(0, TITLE_MAX - 1).trimEnd() + '…';
}

function loadCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .slice(0, MAX_THREADS)
      .map(t => ({ ...t, turns: Array.isArray(t.turns) ? t.turns : [] }));
  } catch (_) {
    return [];
  }
}

// Persist with quota safety: drop the oldest threads and retry if storage is
// full rather than silently losing everything.
function saveCache(arr) {
  let data = arr.slice(0, MAX_THREADS);
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return;
    } catch (_) {
      if (data.length <= 1) return;
      data = data.slice(0, Math.max(1, Math.floor(data.length / 2)));
    }
  }
}

// Map a backend thread record to the shape the UI expects. The id of the
// thread IS the backend conversation_id, so reopening the thread reuses the
// same memory bucket on the next WebSocket connect.
//
// The backend scopes this list by the verified Keycloak subject. Local cache
// data is merged only to restore richer response blocks on this device; a
// server thread without a local match is still safe to display and hydrate.
function mergeServerWithCache(serverThreads, cachedThreads) {
  const cacheById = new Map(cachedThreads.map(t => [t.conversationId || t.id, t]));
  return serverThreads
    .map(s => {
      const cached = cacheById.get(s.conversation_id);
      const createdAt = s.first_seen ? Date.parse(s.first_seen) : Date.now();
      const updatedAt = s.last_seen  ? Date.parse(s.last_seen)  : createdAt;
      return {
        id: s.conversation_id,
        conversationId: s.conversation_id,
        title: truncate(s.title || (cached && cached.title) || 'New chat'),
        createdAt,
        updatedAt,
        turnCount: s.turn_count,
        turns: cached && Array.isArray(cached.turns) ? cached.turns : [],
      };
    });
}

export function usePallasThreads() {
  // Seed from localStorage so the sidebar isn't empty during the network hop.
  const [threads, setThreads] = useState(loadCache);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [backendStatus, setBackendStatus] = useState('loading');
  const activeRef = useRef(activeThreadId);

  useEffect(() => { activeRef.current = activeThreadId; }, [activeThreadId]);
  // Keep the local cache in sync on every change so own-device fidelity
  // survives a hard reload.
  useEffect(() => { saveCache(threads); }, [threads]);

  // Pull the authoritative list from the backend on mount. If it fails we
  // keep whatever seed we already have — the cache is the offline fallback.
  const refreshFromBackend = useCallback(async () => {
    try {
      const data = await fetchThreads(MAX_THREADS);
      const serverThreads = Array.isArray(data && data.threads) ? data.threads : [];
      setThreads(prev => mergeServerWithCache(serverThreads, prev));
      setBackendStatus('ok');
    } catch (e) {
      // Backend unreachable / 401 / 404 — keep the local cache visible
      // rather than blanking the sidebar. Logs once so dev sees the cause.
      console.warn('[threads] backend unavailable, using local cache:', e?.message || e);
      setBackendStatus('offline');
    }
  }, []);

  useEffect(() => { refreshFromBackend(); }, [refreshFromBackend]);

  // Cross-device sync: when the user comes back to this tab, refresh the list.
  // Cheaper than a constant poll and catches the common "I sent a message on
  // my laptop, now I'm checking on my desktop" workflow.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshFromBackend();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshFromBackend]);

  // When the user opens a thread that has no cached turns (started on another
  // device), pull the server-stored turn summaries so they at least see the
  // past queries, tool used, and short result summaries. Full response blocks
  // aren't server-stored — that's a known limit, see the file header.
  const openThread = useCallback(async (id) => {
    setActiveThreadId(id);
    activeRef.current = id;
    setThreads(prev => {
      const t = prev.find(x => x.id === id);
      if (!t || (t.turns && t.turns.length > 0)) return prev;
      // Fire-and-forget hydrate. We don't await inside setState; the next
      // setThreads below will merge in the fetched turns.
      fetchThreadTurns(t.conversationId || id)
        .then(data => {
          const stored = Array.isArray(data && data.turns) ? data.turns : [];
          if (stored.length === 0) return;
          setThreads(cur => cur.map(x => x.id === id ? {
            ...x,
            turns: stored.map(s => ({
              id: genId(),
              query: s.query || '',
              // Mark hydrated turns so ResponsePanel can render them as a
              // summary card rather than expecting full blocks/metadata.
              response: { hydrated: true, summary: s.result_summary,
                          tool: s.tool_used, type: s.result_type,
                          recordCount: s.record_count },
              at: s.timestamp ? Date.parse(s.timestamp) : Date.now(),
            })),
          } : x));
        })
        .catch(err => {
          console.warn('[threads] hydrate turns failed:', err?.message || err);
        });
      return prev;
    });
  }, []);

  const clearActive = useCallback(() => {
    setActiveThreadId(null);
    activeRef.current = null;
  }, []);

  // Delete: confirm first (this hits OpenSearch and is irreversible). Optimistic
  // local removal so the row disappears immediately; the backend delete is
  // best-effort — a stuck thread is a worse UX than a thread that briefly
  // reappears on the next refresh if the call failed.
  const deleteThread = useCallback(async (id) => {
    const target = threads.find(t => t.id === id);
    if (!target) return;
    const ok = (typeof window === 'undefined' || !window.confirm) ? true
      : window.confirm(`Delete thread "${target.title}"? This removes it from every device for this analyst.`);
    if (!ok) return;
    const convId = target.conversationId || id;
    setThreads(prev => prev.filter(t => t.id !== id));
    setActiveThreadId(cur => (cur === id ? null : cur));
    try {
      await deleteThreadOnServer(convId);
    } catch (e) {
      console.warn('[threads] backend delete failed (kept local removal):', e?.message || e);
    }
  }, [threads]);

  // Rename: the new name shows at once and is stored with the chat, so a reload
  // or another device keeps it (it was local-only and came back on reload,
  // 11 Oct). A failed store keeps the local name, as delete keeps its removal.
  const renameThread = useCallback(async (id, title) => {
    const target = threads.find(t => t.id === id);
    setThreads(prev => prev.map(t => (t.id === id ? { ...t, title: truncate(title) } : t)));
    if (!target) return;
    try {
      await renameThreadOnServer(target.conversationId || id, title);
    } catch (e) {
      console.warn('[threads] backend rename failed (kept local name):', e?.message || e);
    }
  }, [threads]);

  // Append a completed turn to the active thread. With the backend now
  // authoritative for the LIST, this primarily updates the local cache so
  // ResponsePanel keeps full visual fidelity for own-device threads. The
  // chat WS write to OpenSearch is what makes the thread show up in the
  // sidebar across devices — that's owned by the backend, not this hook.
  // `conversationId` here is the backend convo ID; the thread's `id`
  // tracks it so reopening keeps the same memory bucket.
  const commitTurn = useCallback((query, response, conversationId) => {
    if (!response) return;
    const now = Date.now();
    const turn = { id: genId(), query: query || '', response, at: now };
    setThreads(prev => {
      let id = activeRef.current;
      const exists = id && prev.some(t => t.id === id);
      // First turn of a fresh chat: use the backend's conversation_id as
      // the thread id so the sidebar entry produced by the next backend
      // refresh deduplicates against this one.
      if (!exists) {
        id = conversationId || genId();
        activeRef.current = id;
        Promise.resolve().then(() => setActiveThreadId(id));
        return [{
          id,
          conversationId: conversationId || null,
          title: truncate(query),
          createdAt: now,
          updatedAt: now,
          turnCount: 1,
          turns: [turn],
        }, ...prev].slice(0, MAX_THREADS);
      }
      const target = prev.find(t => t.id === id);
      const others = prev.filter(t => t.id !== id);
      const isUntitled = !target.title || target.title === 'New chat';
      const updated = {
        ...target,
        conversationId: target.conversationId || conversationId || null,
        turns: [...(target.turns || []), turn].slice(-MAX_TURNS),
        turnCount: (target.turnCount || 0) + 1,
        updatedAt: now,
        title: isUntitled ? truncate(query) : target.title,
      };
      return [updated, ...others].slice(0, MAX_THREADS);
    });
  }, []);

  const activeThread = threads.find(t => t.id === activeThreadId) || null;

  return {
    threads,
    activeThreadId,
    activeThread,
    backendStatus,        // 'loading' | 'ok' | 'offline' — for an optional badge
    openThread,
    clearActive,
    deleteThread,
    renameThread,
    commitTurn,
    refreshFromBackend,   // call after sending a message if you want the
                          // sidebar to reflect a brand-new thread immediately
  };
}

export default usePallasThreads;
